/**
 * Page copy for /industries/fintech.
 *
 * Source: page-industry-fintech.md, supplied by the client.
 *
 * Decisions taken with the client:
 *
 * 1. METRICS. §6 tags AI Test Case Generation as "a WeAreQA engagement", so the
 *    three figures ship here as a WeAreQA client outcome. The /industries hub
 *    keeps "Work our team has led" on all five blocks, Fintech included — the
 *    two pages differ deliberately, and nothing is overstated on either.
 * 2. §4 DORA. Built verbatim. The source's own note says to verify it against
 *    current guidance before publishing; it is the only regulatory claim on the
 *    site. Flagged in CLAUDE.md rather than held back.
 * 3. SERVICE LINKS. The source's hrefs (/services/test-automation-architecture,
 *    /services/ai-testing-services, /services/managed-qa-services) are earlier
 *    slugs. Repointed at the routes that exist. Labels unchanged.
 *
 * NO PLACEHOLDERS LEFT (2026-08-19). Client shape came from the deck, the price
 * slot was removed, and §4 gained a researched UK framing block. See the notes
 * at each.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "Fintech & Financial Services Software Testing",
  description:
    "Quality engineering for core banking, trading, wealth and lending. Evidence produced as a by-product of testing, not assembled for the audit afterward.",
} as const;

export const hero = {
  breadcrumb: { label: "Industries", href: "/industries" },
  badge: "Fintech & Financial Services",
  heading: "Quality Engineering for Financial Services",
  /** Split at its own sentence boundaries so the third clause can land alone. */
  situation: [
    "Your regression cycle is the thing standing between you and a release.",
    "It is also the thing standing between you and an audit finding.",
    "So it grew, because both pressures push the same direction — and now it takes weeks.",
  ],
  segments: ["Core banking", "Trading & wealth", "Lending & credit"],
  ground: ["SOC 2", "DORA Article 26"],
  segmentsLabel: "Segments",
  groundLabel: "Regulatory ground",
} as const;

/**
 * §2 — the page's argument. The source calls this section "the page": everything
 * else supports the claim that compliance-driven testing produces less safety.
 * The chain is four steps ending in a consequence, so it renders as a chain.
 */
export const argument = {
  heading: "Why does compliance slow down software releases?",
  display: "Compliance-driven testing is making you less safe",
  follow: "Follow the chain.",
  chain: [
    "Testing gets organised around producing audit evidence.",
    "That makes it slow.",
    "Slow makes releases rare.",
    "Rare makes each release large.",
  ],
  chainClose: "Large releases fail harder, and they fail in ways that are harder to diagnose.",
  consequence:
    "So the spend went up, the cycle got longer, and the actual risk — a bad change reaching production with a dozen other changes hiding it — went up too.",
  turn:
    "The firms that broke out of this didn't test less. They stopped treating evidence as a separate exercise performed after the fact.",
  claim:
    "When evidence is a by-product of testing rather than a project of its own, you can release weekly and produce a better audit trail than the firm releasing quarterly.",
  claimTail:
    "Traceability generated automatically from requirement to test to result beats a spreadsheet assembled from memory in week eleven — for you and for whoever reviews it.",
  close: "That's the shift this page is about.",
} as const;

export type Breakage = { lead: string; tail: string; icon: IconName };

export const breaks = {
  heading: "Why is testing so slow in banking and financial services?",
  display: "What actually breaks",
  items: [
    {
      lead: "The core doesn't cooperate.",
      tail: " Batch windows, overnight processing, and a mainframe or vendor platform that has no test instance you can reset. Everything queues behind it.",
      icon: "database",
    },
    {
      lead: "You can't test with real data.",
      tail: " PII rules it out, and the synthetic data you have isn't shaped like reality — so the defects that matter only appear in production, where the data is real.",
      icon: "shieldCheck",
    },
    {
      lead: "Reconciliation is where the bugs live.",
      tail: " Individual services pass. The numbers still disagree at end of day, and nobody's tests span the boundary where they disagree.",
      icon: "cost",
    },
    {
      lead: "Evidence is assembled, not generated.",
      tail: " Someone spends the last two weeks of every cycle building a traceability pack by hand.",
      icon: "fileText",
    },
    {
      lead: "Decisioning logic is undertested.",
      tail: " Lending rules and pricing models get exercised on happy paths, and the edge cases are discovered by customers or by a regulator asking about outcomes.",
      icon: "sequence",
    },
  ] as Breakage[],
  close: "If four of those are true, adding testers will not fix it. The constraint is structural.",
} as const;

/**
 * §4 — the only regulatory claim on the site. Verbatim, and due a re-check
 * against current guidance before the domain goes live. See CLAUDE.md.
 */
export const dora = {
  heading: "What does DORA require for software testing?",
  display: "DORA changed what testing means",
  requirement: {
    strong:
      "Article 26 requires every in-scope financial entity to run a digital operational resilience testing programme",
    tail: " — risk-based, covering critical tools and applications at least annually, with weaknesses identified and closed.",
  },
  commercial: {
    lead: "Two details matter commercially. ",
    strong: "The regulation requires tests to be conducted by independent parties",
    tail: ", internal or external — which is what an external quality partner structurally provides. And since January 2025 this has been enforceable rather than upcoming; through 2026 supervisors moved from guidance to assessment.",
  },
  ai: {
    strong:
      "BaFin's January 2026 guidance confirmed that AI systems, including generative AI and LLMs, sit inside existing ICT testing and third-party risk frameworks",
    tail: " rather than under a separate regime. If you have shipped AI features, they are in scope, and most testing programmes have not caught up.",
  },
  limit: {
    label: "Where we stop.",
    lead: " We support Article 26 — scenario-based testing, resilience validation, evidence generation, and bringing AI components into the testing scope. ",
    strong: "We do not perform threat-led penetration testing under Article 27.",
    tail: " TLPT is specialist red-team work under TIBER-EU, and you should engage a specialist for it. We'll say so on the first call.",
  },
  /**
   * UK FRAMING. Replaces the placeholder that asked "how should this page read
   * for a UK prospect?" — /about says the company sells into the US, Canada,
   * UK and Ireland, and §4 built its whole argument on a regulation binding one
   * of those four markets. A UK reader could reasonably conclude the page was
   * not addressed to them.
   *
   * **NEW COPY, AND IT IS REGULATORY. It needs a compliance read before the
   * domain goes live — not just a copy sign-off.** Written 2026-08-19 against
   * sources checked the same day:
   *
   *   - DORA applies from 17 January 2025 to EU financial entities. UK-regulated
   *     firms operating solely in the UK are not directly in scope.
   *   - UK firms with EU subsidiaries or branches must ensure those entities
   *     comply, including the ICT third-party obligations.
   *   - The UK equivalent is FCA PS21/3 and PRA SS1/21 — impact tolerances for
   *     important business services. The transition period closed 31 March 2025
   *     and firms are expected to be fully compliant.
   *   - For firms in scope on both sides, work meeting DORA's standard
   *     generally satisfies FCA and PRA expectations; the reverse is not
   *     reliably true.
   *
   * NOT INCLUDED, deliberately: the UK critical third parties regime under FSMA
   * 2023. As of late 2025 no designations had been made and initial ones were
   * only anticipated. Too unsettled to assert on a marketing page.
   *
   * Same quarterly re-check as the rest of §4.
   */
  uk: {
    label: "If you're in the UK.",
    lead: " DORA binds EU entities, so an Irish firm is directly in scope and a UK-only firm is not. UK operational resilience runs on its own track: ",
    strong:
      "FCA PS21/3 and PRA SS1/21, where the test is whether you can stay within impact tolerances for important business services",
    tail: " — and that transition period closed in March 2025. If you have EU subsidiaries or branches, those entities are in DORA scope whatever the group's domicile. Work built to DORA's standard generally satisfies FCA and PRA expectations, and the reverse is not reliably true — so for a group operating on both sides, DORA is the safer bar to build to.",
  },
} as const;

export type Capability = { lead: string; tail: string; icon: IconName };

export const capabilities = {
  display: "What we do",
  items: [
    {
      lead: "Coverage where the money moves.",
      tail: "Reconciliation and settlement paths, not just service-level happy paths. The tests that span boundaries are the ones that catch the defects that cost you.",
      icon: "target",
    },
    {
      lead: "Test data that behaves like production without being production.",
      tail: "Synthetic data shaped to real distributions — the edge cases, the malformed records, the accounts with twenty years of history — provisioned per run and torn down after.",
      icon: "database",
    },
    {
      lead: "Evidence generated, not assembled.",
      tail: "Requirement-to-test-to-result traceability produced automatically by the pipeline. The audit pack is a report you run, not a project you staff.",
      icon: "fileText",
    },
    {
      lead: "Risk-based prioritisation.",
      tail: "What gets tested first is driven by change impact and business exposure, not by whichever suite someone wrote last year.",
      icon: "gauge",
    },
  ] as Capability[],
  deliveryLead: "Delivered through ",
  delivery: [
    {
      label: "Test Architecture",
      href: "/services/qa-architecture-engineering",
      tail: " when the constraint is structural, ",
    },
    {
      label: "AI-Driven Validation",
      href: "/services/ai-driven-validation",
      tail: " when it's test design, or ",
    },
    {
      label: "Intelligent Quality Operations",
      href: "/services/intelligent-quality-operations",
      tail: " when you need the function run.",
    },
  ],
} as const;

/**
 * §6 — the page's only unqualified proof, and the one case study the client has
 * tagged as WeAreQA's own. The figures carry no "work our team has led" label
 * here for that reason; the hub still does.
 */
export const proof = {
  display: "Proof",
  name: "AI Test Case Generation",
  attribution: "A WeAreQA engagement",
  /**
   * From `WeAreQA-AI-Driven.pdf` slide 19, where this study appears in the AI
   * in Action Portfolio as "01 AI TEST CASE GENERATION — Global Financial
   * Services Company". Same source as the two shapes on /industries/ecommerce.
   *
   * DELIBERATELY ABSENT: engineer count, segment (core banking, trading or
   * lending) and release cadence. The placeholder asked for all three and its
   * suggested wording — "A European wealth management platform, ~250
   * engineers, releasing monthly" — was labelled "Example:" because it is
   * illustration. They are factual claims about a real engagement, nobody has
   * supplied the real ones, and they are not invented here.
   */
  shape: "A global financial services company.",
  narrative:
    "Manual test design was consuming senior engineering time on every release. We put test scenario generation on models trained against their own requirements and defect history, with risk-based prioritisation deciding what got built first.",
  metrics: [
    { value: "60%", label: "Faster test design" },
    { value: "35%", label: "More test coverage" },
    { value: "40%", label: "Less manual effort" },
  ],
  /** "faster *and* covering more" — the emphasis is the source's own. */
  read: {
    lead: "The coverage number is the one to look at. Faster test design is easy if you write fewer tests. Doing it faster ",
    emphasis: "and",
    tail: " covering more is where the design work paid off.",
  },
  /**
   * NO "Read the full case study" LINK. Removed at the client's request.
   *
   * It was `href="#"` — it pointed at one of the five case studies the client
   * rejected as wrong and deleted, and no replacement has landed. /case-studies
   * is not a substitute: that hub lists the three AI use-case studies, not this
   * one.
   *
   * The same dead link is still on /industries/healthcare and
   * /industries/communications.
   */
} as const;

export const businessCase = {
  display: "The business case",
  note: "For the conversation with your CEO or board.",
  columns: { today: "Today", after: "After" },
  rows: [
    {
      label: "Release frequency",
      today: "Constrained by the regression cycle",
      after: "Constrained by product decisions",
    },
    {
      label: "Audit preparation",
      today: "A project, every cycle",
      after: "A report, on demand",
    },
    {
      label: "Risk per release",
      today: "High — large batches, coupled changes",
      after: "Lower — small batches, isolated changes",
    },
    {
      label: "DORA Article 26",
      today: "A gap to close",
      after: "An existing programme with evidence",
    },
  ],
  closeStrong: "The number that matters isn't the testing budget.",
  closeTail:
    " It's what a single material incident costs — remediation, regulatory attention, and the twelve months of supervisory interest that follows. That's what this is competing against.",
} as const;

export const notFit = {
  display: "Where we're not the right fit",
  items: [
    {
      lead: "Payments and PSP work.",
      tail: " Our financial services experience is core banking, trading and wealth, and lending. We don't claim PCI-DSS scope because we don't operate in it.",
    },
    {
      lead: "Threat-led penetration testing.",
      tail: " Article 27 TLPT is specialist work. Not ours.",
    },
    {
      lead: "Tier-1 global banks.",
      tail: " We're a small senior team. We'd be a subcontractor at that scale, and you'd be better served by someone who does that well.",
    },
  ],
} as const;

/**
 * §9. NO PRICE, BY DECISION — consistent with the four service pages and
 * /industries/ecommerce. The placeholder was removed at the client's request.
 *
 * **WHAT IS LEFT IS A HEADING AND TWO BUTTONS.** This section has no body copy;
 * the placeholder was the only thing between "Starting from" and the CTAs. So
 * it announces a price and then shows a call to action instead of one.
 *
 * CONSIDERED AND DECLINED, 2026-08-19. It reads as a closing CTA — centred,
 * yellow accent bar, the same two buttons the industries hub closes with — and
 * one line replacing `display` would have made it one. The client chose to
 * leave the heading as the source wrote it. Do not revisit without asking.
 *
 * ALL FIVE INDUSTRY PAGES SHARE THIS SECTION — ecommerce and fintech are now in
 * this state; healthcare, pharma and communications still hold the placeholder.
 */
export const pricing = {
  display: "Starting from",
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "AI Maturity Model",
      tail: "Where your quality function sits today",
      href: "/#assessment",
    },
    {
      label: "Test Architecture & QA Platform Engineering",
      tail: "",
      href: "/services/qa-architecture-engineering",
    },
    { label: "AI-Driven Validation", tail: "", href: "/services/ai-driven-validation" },
    {
      label: "Intelligent Quality Operations",
      tail: "",
      href: "/services/intelligent-quality-operations",
    },
  ],
} as const;
